/**
 * NetworkInformation interface for browser connection API
 * Not all browsers support all properties
 */
interface NetworkInformation {
  effectiveType?: '2g' | '3g' | '4g' | '5g' | 'slow-2g';
  saveData?: boolean;
  metered?: boolean;
  downlink?: number;
}

/**
 * Navigator interface with connection API support
 */
interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

/**
 * Determines optimal image quality based on user's connection speed
 * Returns quality value as a string (1-100)
 *
 * Higher quality for faster connections:
 * - Default: 85% quality (good balance)
 * - SaveData mode: 50% quality (data saving)
 * - Metered: 70% quality (conservative)
 * - Connection specific values range from 40% (slow-2g) to 100% (5g)
 */
export const getImageQuality = (): string => {
  // Server-side rendering check
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return '85';
  }

  // Get connection info with fallbacks for different browser implementations
  let connection: NetworkInformation | null = null;

  const nav = navigator as NavigatorWithConnection;

  if ('connection' in nav && nav.connection) {
    connection = nav.connection;
  } else if ('mozConnection' in nav && nav.mozConnection) {
    connection = nav.mozConnection;
  } else if ('webkitConnection' in nav && nav.webkitConnection) {
    connection = nav.webkitConnection;
  }

  // Default quality if no connection info available
  if (!connection) return '95';

  // Data saving modes
  if (connection.saveData) return '50';
  if (connection.metered) return '70';

  // Connection speed based quality
  if (connection.effectiveType) {
    switch (connection.effectiveType) {
      case 'slow-2g':
        return '40';
      case '2g':
        return '60';
      case '3g':
        return '75';
      case '4g':
        return '90';
      case '5g':
        return '100';
      default:
        return '85';
    }
  }

  // Fallback to bandwidth-based quality if available
  if (connection.downlink) {
    if (connection.downlink < 1) return '60'; // Less than 1 Mbps
    if (connection.downlink < 5) return '75'; // 1-5 Mbps
    if (connection.downlink < 10) return '85'; // 5-10 Mbps
    if (connection.downlink < 20) return '90'; // 10-20 Mbps
    return '95'; // > 20 Mbps
  }

  // Default quality
  return '85';
};

export default getImageQuality;
